import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/lib/tracking";

/**
 * Mounts a single global listener that:
 *  - records a page_view on every route change (debounced per path)
 *  - records phone_click / email_click / maps_click / whatsapp_click on
 *    delegated link clicks anywhere in the document.
 */
export function useTrackingListener() {
  const location = useLocation();
  const lastTrackedPath = useRef<string>("");

  // Page views
  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastTrackedPath.current === path) return;
    lastTrackedPath.current = path;
    trackEvent("page_view", { metadata: { hash: location.hash || undefined } });
    if (location.pathname === "/menu") {
      trackEvent("menu_view", { section: "menu_page" });
    }
  }, [location]);

  // Delegated click listener for outbound contact links
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (!href) return;

      // Find a meaningful source section (closest [id] or [data-section])
      const sectionEl =
        anchor.closest<HTMLElement>("[data-section]") ||
        anchor.closest<HTMLElement>("section[id]") ||
        anchor.closest<HTMLElement>("[id]");
      const section =
        sectionEl?.dataset?.section ||
        sectionEl?.id ||
        undefined;

      if (href.startsWith("tel:")) {
        trackEvent("phone_click", {
          section,
          metadata: { href, label: anchor.textContent?.trim().slice(0, 120) },
        });
      } else if (href.startsWith("mailto:")) {
        trackEvent("email_click", {
          section,
          metadata: { href, label: anchor.textContent?.trim().slice(0, 120) },
        });
      } else if (/^https?:\/\/(maps\.app\.goo\.gl|www\.google\.[^/]+\/maps|maps\.google\.)/i.test(href)) {
        trackEvent("maps_click", { section, metadata: { href } });
      } else if (/^https?:\/\/(wa\.me|api\.whatsapp\.com)/i.test(href)) {
        trackEvent("whatsapp_click", { section, metadata: { href } });
      }
    };

    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true });
  }, []);
}

export default function TrackingMount() {
  useTrackingListener();
  return null;
}
